import { Injectable, InjectProperty } from '@jack-henry/frontend-utils/di';
import { HttpResponse } from '@jack-henry/frontend-utils/types';
import {
    ApplicationNameTypeDto,
    BackOfficeUserListModelDto,
    CompaniesClient,
    LookupModelDto,
    UserActivityTypeDto,
    UserModelDto,
    UsersClient,
} from '@treasury/api/bo';
import Fuse from 'fuse.js';
import { uniqWith } from 'lodash';
import { CompanyUserSummary } from '../../../company/user/entities/company-user-summary.entity';
import { AuthenticationService } from '../../../services/authentication';
import { Feature, FeatureFlagService } from '../../../services/feature-flags';
import { FiUser } from '../../../users/fi-user.entity';
import { mapUserTimeAccess } from '../../mappings/time-access/map-user-time-access-response';
import UserRequests from '../../requests/users/user-requests.js';
import { User } from './user.entity';

export { UserActivityTypeDto as UserActivityTypes } from '@treasury/api/bo';

type UserActivityTypes = UserActivityTypeDto;
export enum UserSearchField {
    UserName = 'UserName',
    DigitalId = 'DigitalId',
    UserAlias = 'UserAlias',
}

@Injectable()
export class UsersService {
    constructor(
        private readonly companiesClient: CompaniesClient,
        private usersClient: UsersClient,
        private authenticationService: AuthenticationService
    ) {}

    // #region Legacy Static Methods

    public static changeUserStatus(userPayload: unknown, newStatus: string) {
        return UserRequests.changeUserStatus(userPayload, newStatus);
    }

    public static deleteUser(userId: number) {
        return UserRequests.deleteUser(userId);
    }

    public static forgotPassword(userPayload: unknown) {
        return UserRequests.forgotPassword(userPayload);
    }

    public static getActiveUsers() {
        return UserRequests.getActiveUsers();
    }

    public static async getCurrentUserTimeAccess() {
        const response = await UserRequests.getCurrentUserTimeAccess();
        return mapUserTimeAccess(response);
    }

    public static getNewUserTimeAccess() {
        return UserRequests.getNewUserTimeAccess();
    }

    public static getUser(userId: number) {
        return UserRequests.getUser(userId);
    }

    public static getUserLoginTokenDetail(userId: number) {
        return UserRequests.getUserLoginTokenDetail(userId);
    }

    public static getUsers() {
        return UserRequests.getUsers();
    }

    public static getAllUsers() {
        return UserRequests.getAllUsers();
    }

    public static async getUsersByCompanyIds(companyIds: string) {
        const users = await UserRequests.getUsersByCompanyIds(companyIds);
        const index = users.findIndex((user: { userId: string }) => user.userId === 'System');
        const [systemUser] = users.splice(index, 1);
        type User = { userName: string };
        users.sort((a: User, b: User) => a.userName.localeCompare(b.userName));
        users.unshift(systemUser);
        return users;
    }

    public static manualResetPassword(userId: string, passwordPayload: unknown) {
        return UserRequests.manualResetPassword(userId, passwordPayload);
    }

    public static resetPassword(userId: number) {
        return UserRequests.resetPassword(userId);
    }

    public static resetSecureToken(userId: number) {
        return UserRequests.resetSecureToken(userId);
    }

    public static saveUser(userPayload: unknown) {
        return UserRequests.saveUser(userPayload);
    }

    public static unlock(userId: number) {
        return UserRequests.unlock(userId);
    }

    public static unlockSecureToken(userId: number, recollectToken: string) {
        return UserRequests.unlockSecureToken(userId, recollectToken);
    }

    public static updateUser(userPayload: unknown) {
        return UserRequests.updateUser(userPayload);
    }

    // #endregion

    @InjectProperty({
        token: FeatureFlagService,
        async: true,
    })
    declare private ffServicePromise: Promise<FeatureFlagService>;

    /**
     * Sets the active status of a user.
     * @param userId The ID of the user to update.
     * @param setActive The new active status of the user.
     * @returns The result of the status change.
     */
    public async setUserActiveStatus(userId: number, setActive: boolean) {
        return this.usersClient.usersChangeStatus(userId, setActive ? 0 : 1);
    }

    public async unlockUser(user: BackOfficeUserListModelDto) {
        const updateUser = (await this.usersClient.usersGet(user.id)).data;
        return this.usersClient.usersPut(updateUser.id, { ...updateUser, isLocked: false });
    }

    public async deleteUser(userId: number) {
        return this.usersClient.usersDelete(userId);
    }

    public async manualResetPassword(userId: number, passwordPayload: string) {
        return this.usersClient.usersManualResetPassword(userId, {
            key: passwordPayload,
            value: passwordPayload,
        } as LookupModelDto);
    }

    public async resetPassword(userId: number) {
        return this.usersClient.usersResetPassword(userId);
    }

    public async searchFiUsers(
        searchText: string,
        searchType: UserSearchField = UserSearchField.UserName
    ) {
        const resp = await this.usersClient.usersSearchAllChannelUser({ searchText, searchType });
        return resp.data.map(u => new User(u));
    }

    public async fuzzySearchFiUsers(searchText: string) {
        const response = await Promise.all([
            this.searchFiUsers(searchText, UserSearchField.UserName),
            this.searchFiUsers(searchText, UserSearchField.DigitalId),
            this.searchFiUsers(searchText, UserSearchField.UserAlias),
        ]);
        const dedupedUsers = uniqWith(response.flat(), (a, b) => a.userId === b.userId);
        const fuse = new Fuse(dedupedUsers, {
            keys: ['userName', 'digitalId', 'userAlias'],
        });
        return fuse.search(searchText).map(result => result.item);
    }

    public async saveUserActivity(
        userActivityType: UserActivityTypes,
        activityMessage: string,
        relatedId = undefined,
        activityDate: Date = new Date()
    ) {
        const response = await this.usersClient.usersSaveUserActivity({
            userActivityType: userActivityType as UserActivityTypeDto,
            activityMessage,
            relatedId,
            activityDate: activityDate.toISOString(),
            applicationNameType: ApplicationNameTypeDto.TreasuryBackOffice,
        });
        return response.data;
    }

    public async getCompanyUsers(companyId: number, enrolledAndActive: boolean) {
        const uisEnabled = await (await this.ffServicePromise).isEnabled(Feature.UisEnabled);
        const usersEndpoint = uisEnabled
            ? this.companiesClient.companiesGetUsers(companyId, enrolledAndActive, {})
            : this.companiesClient.companiesUsersAll(companyId, enrolledAndActive, {});

        const response = await usersEndpoint;
        const users = response.data.map(user => new CompanyUserSummary(user));
        return users;
    }

    public async getUsers(): Promise<FiUser[]> {
        const getUsersModel = (await this.usersClient.usersGet2()).data;
        return FiUser.fromGetUsersModelDto(getUsersModel);
    }

    public async getUserById(id: number): Promise<FiUser> {
        return FiUser.fromGetUserById((await this.usersClient.usersGet(id)).data);
    }

    public async getUserByUserName(userName: string): Promise<FiUser> {
        const users = await this.getUsers();
        const result = users.find(user => user.userId === userName);
        if (!result) {
            throw new Error(`User with userName ${userName} not found`);
        }
        return result;
    }

    public async getCurrentUser(): Promise<FiUser> {
        const { loginId } = await this.authenticationService.getCurrentUser();
        const user = await this.getUserByUserName(loginId);
        return user;
    }

    public async saveUser(user: FiUser): Promise<FiUser> {
        const userDto = user.toSaveDto();
        const response = await this.usersClient.usersPost(userDto as UserModelDto);
        return new FiUser(response.data);
    }

    public async updateUser(user: FiUser): Promise<FiUser> {
        const userDto = user.toSaveDto();
        const response = await this.usersClient.usersPut(user.id, userDto as UserModelDto);
        return new FiUser(response.data);
    }

    public async changeUserStatus(id: number, isActive: boolean): Promise<HttpResponse> {
        return await this.usersClient.usersChangeStatus(id, isActive ? 0 : 1);
    }
}
